/* =========================================================================
   Seed data — Kylie's 4 real recipes, rescued from iteration 1.

   The 6 invented sample recipes (s1-s6) from iteration 1 have been removed.
   Two real recipes had been written into that SAMPLES array by mistake, which
   meant "Reset to samples" would have destroyed the other two; they are here
   as first-class recipes now, so reset is safe.

   Photos are not seeded — import your exported collection to bring them in.
   ========================================================================= */

export const SEED_RECIPES = [
  {
    "id": "r-cypriotgrain1",
    "title": "Hellenic Republic's Cypriot Grain Salad",
    "category": "Salad",
    "emoji": "🥗",
    "tags": [
      "healthy",
      "vegetarian",
      "BBQ side",
      "make-ahead",
      "under 30 min"
    ],
    "serves": 8,
    "source": {
      "name": "Travis McAuley — Hellenic Republic (Good Food / Sydney Morning Herald)",
      "url": "https://www.smh.com.au/goodfood/recipes/hellenic-republics-cypriot-grain-salad-20130321-2gh6d.html"
    },
    "ingredients": [
      {
        "qty": 1,
        "unit": "bunch",
        "item": "coriander, chopped"
      },
      {
        "qty": 0.5,
        "unit": "bunch",
        "item": "parsley, chopped"
      },
      {
        "qty": 0.5,
        "unit": "",
        "item": "red onion, finely diced"
      },
      {
        "qty": 1,
        "unit": "cup",
        "item": "freekeh (or cracked wheat or quinoa)"
      },
      {
        "qty": 0.5,
        "unit": "cup",
        "item": "Puy lentils"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "toasted pumpkin seeds"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "toasted slivered almonds"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "toasted pine nuts"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "baby capers"
      },
      {
        "qty": 0.5,
        "unit": "cup",
        "item": "currants"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "lemon, juiced"
      },
      {
        "qty": 3,
        "unit": "tbsp",
        "item": "extra virgin olive oil"
      },
      {
        "qty": null,
        "unit": "",
        "item": "sea salt, to taste"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "pomegranate, deseeded, to serve"
      },
      {
        "qty": 1,
        "unit": "cup",
        "item": "thick Greek yoghurt (dressing)"
      },
      {
        "qty": 1,
        "unit": "tsp",
        "item": "cumin seeds, toasted and ground (dressing)"
      },
      {
        "qty": 1,
        "unit": "tbsp",
        "item": "honey (dressing)"
      }
    ],
    "method": [
      "Blanch freekeh and lentils separately in boiling water until both just cooked.",
      "Drain well and allow to cool.",
      "Mix the yoghurt, ground cumin and honey until combined.",
      "In a medium bowl, place the coriander, parsley, red onion, freekeh, lentils, toasted nuts and seeds, capers, currants, lemon juice and olive oil. Mix well and season to taste.",
      "Place into a serving dish and top with the cumin yoghurt and pomegranate seeds."
    ],
    "notes": [
      {
        "text": "Original: easy, under 30 minutes, serves 8. Freekeh is roasted green wheat — whole wheat, cracked wheat or quinoa all substitute well.",
        "date": "2026-07-30"
      },
      {
        "text": "The last three ingredients (yoghurt, cumin, honey) are the dressing — spooned on top at serving, not mixed through.",
        "date": "2026-07-30"
      }
    ],
    "pairings": [
      {
        "text": "A classic alongside BBQ lamb or chicken — great summer barbecue side.",
        "date": "2026-07-30"
      }
    ],
    "createdAt": "2026-07-30T00:00:00.000Z",
    "pos": 0
  },
  {
    "id": "r-fattoush1",
    "title": "Sweet Green Pea Fattoush",
    "category": "Salad",
    "emoji": "🥗",
    "tags": [
      "quick",
      "vegetarian",
      "midweek",
      "crunchy"
    ],
    "serves": 2,
    "source": {
      "name": "Natalie Chassay — A Way With Food (Substack), 26 Jul 2026",
      "url": "https://nataliechassay.substack.com/p/a-green-pea-fattoush-to-remember"
    },
    "ingredients": [
      {
        "qty": 2,
        "unit": "",
        "item": "small pita breads (or Lebanese bread), torn into bite-sized pieces"
      },
      {
        "qty": 3,
        "unit": "tbsp",
        "item": "extra virgin olive oil (for the pita chips)"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "za'atar"
      },
      {
        "qty": 400,
        "unit": "g",
        "item": "frozen green peas"
      },
      {
        "qty": 5,
        "unit": "",
        "item": "baby cucumbers (or 2 Lebanese cucumbers), cut into chunky rounds"
      },
      {
        "qty": 0.5,
        "unit": "bunch",
        "item": "radishes, finely sliced"
      },
      {
        "qty": 0.5,
        "unit": "",
        "item": "small red onion, paper-thinly sliced"
      },
      {
        "qty": 0.5,
        "unit": "bunch",
        "item": "fresh mint, leaves picked (larger leaves torn)"
      },
      {
        "qty": 0.5,
        "unit": "cup",
        "item": "pomegranate seeds"
      },
      {
        "qty": 2,
        "unit": "tsp",
        "item": "ground sumac"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "juicy lemon, juiced"
      },
      {
        "qty": null,
        "unit": "",
        "item": "extra virgin olive oil, to taste (dressing)"
      },
      {
        "qty": null,
        "unit": "",
        "item": "flaky sea salt, to taste"
      }
    ],
    "method": [
      "Bake the pita chips: preheat oven to 180°C/350°F. Toss torn pita with 3 tbsp olive oil and the za'atar until every piece is coated. Spread in a single layer on a baking tray and bake ~15 minutes until crunchy and golden. (Thin Lebanese bread crisps much faster — watch it.) Cool completely.",
      "Place frozen peas in a colander and pour boiling water over until thawed. Drain and set aside — don't boil them; just thawed keeps them vibrant, sweet and poppy.",
      "In a large mixing bowl combine cucumbers, radishes, red onion and thawed peas. Season generously with flaky salt, sumac, a liberal squeeze of lemon juice and a healthy drizzle of olive oil. Toss gently so the acid softens the raw onion.",
      "Just before serving, fold through the mint, pomegranate seeds and cooled pita chips. Toss so the dressing coats the bread, and serve immediately."
    ],
    "notes": [
      {
        "text": "Original serves 2 as a main, or 4 as a side. Prep 15 min + cook 15 min.",
        "date": "2026-07-30"
      },
      {
        "text": "For sticky-tang depth, whisk 1–2 tsp pomegranate molasses into the dressing. For heat, add a finely diced green chilli with the cucumbers.",
        "date": "2026-07-30"
      },
      {
        "text": "Avoid canned peas at all costs — texture too soft, colour too dull. Double-batch the za'atar pita chips; they keep crunchy for days in an airtight container.",
        "date": "2026-07-30"
      }
    ],
    "pairings": [],
    "pos": 1
  },
  {
    "id": "r-peabutterbean1",
    "title": "Pea + Butter Bean Salad with Pickled Chillies, Feta + Preserved Lemon",
    "category": "Salad",
    "emoji": "🥗",
    "tags": [
      "quick",
      "vegetarian",
      "briny",
      "make-ahead"
    ],
    "serves": 4,
    "source": {
      "name": "Natalie Chassay — A Way With Food (Substack), 15 Jul 2026",
      "url": "https://nataliechassay.substack.com/p/an-abundant-bright-pea-butter-bean"
    },
    "ingredients": [
      {
        "qty": 3,
        "unit": "slices",
        "item": "thick sourdough bread (2–3), torn into chunky pieces"
      },
      {
        "qty": null,
        "unit": "",
        "item": "extra virgin olive oil — a generous glug (plus more for dressing)"
      },
      {
        "qty": 1,
        "unit": "can",
        "item": "butter beans (400g), drained and rinsed"
      },
      {
        "qty": 2,
        "unit": "cups",
        "item": "frozen peas (2–3 cups)"
      },
      {
        "qty": 3,
        "unit": "tbsp",
        "item": "pickled jalapeños, finely chopped (2–4 tbsp, to taste)"
      },
      {
        "qty": 2,
        "unit": "tbsp",
        "item": "jalapeño pickling liquid (2–3 tbsp — start with less)"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "preserved lemon, finely chopped"
      },
      {
        "qty": 1,
        "unit": "handful",
        "item": "fresh dill, roughly chopped (big handful)"
      },
      {
        "qty": 0.5,
        "unit": "",
        "item": "lemon, juiced (½–1 whole, to taste)"
      },
      {
        "qty": 150,
        "unit": "g",
        "item": "feta cheese, crumbled"
      },
      {
        "qty": null,
        "unit": "",
        "item": "sea salt flakes"
      }
    ],
    "method": [
      "Preheat oven to 200°C/400°F. Spread torn sourdough on a baking tray, drizzle generously with olive oil, add a pinch of salt, and bake 10–12 minutes until deeply golden and crisp. Cool.",
      "Put the frozen peas in a bowl or colander, pour over boiling water and blanch about 60 seconds — just to take the chill off, they're already cooked. Drain well.",
      "In a large mixing bowl, combine the butter beans and peas.",
      "Add the chopped pickled jalapeños, preserved lemon and dill.",
      "Dress with the lemon juice, a generous glug of olive oil and the pickling liquid. Add a good pinch of salt and toss. Start with less pickling liquid and build up.",
      "Gently fold through the crumbled feta.",
      "Just before serving, toss in the croutons so they stay crisp. Taste as you go — more pickling liquid or lemon until it sings."
    ],
    "notes": [
      {
        "text": "Original serves 4 as an abundant side, or 2 as a main. Cook time ~15 min.",
        "date": "2026-07-30"
      },
      {
        "text": "Make-ahead: mix beans, peas, herbs and dressing hours ahead and chill — flavours develop. Add feta and croutons only at serving time.",
        "date": "2026-07-30"
      },
      {
        "text": "Subs: cannellini or chickpeas for butter beans; grated fresh lemon zest + extra salt if no preserved lemon. Use Greek feta in brine. Buy frozen peas that rattle loosely in the bag.",
        "date": "2026-07-30"
      }
    ],
    "pairings": [
      {
        "text": "Chilled red wine and grilled chicken — lovely elevated midweek meal.",
        "date": "2026-07-30"
      }
    ],
    "pos": 2
  },
  {
    "id": "r-salmoncitrussoy1",
    "title": "Roast Salmon with Citrus & Soy Dressing and Charred Fennel",
    "category": "Fish",
    "emoji": "🐟",
    "tags": [
      "entertaining",
      "rare salmon",
      "one tray",
      "citrus"
    ],
    "serves": 4,
    "source": {
      "name": "",
      "url": ""
    },
    "ingredients": [
      {
        "qty": 1,
        "unit": "kg",
        "item": "salmon fillet, mid-loin or head end, skin on, pin-boned"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "fennel bulb, sliced 4mm thick lengthways"
      },
      {
        "qty": 1,
        "unit": "bunch",
        "item": "spring onions, green tops trimmed off"
      },
      {
        "qty": 60,
        "unit": "ml",
        "item": "extra virgin olive oil"
      },
      {
        "qty": 1,
        "unit": "tbsp",
        "item": "white wine vinegar"
      },
      {
        "qty": null,
        "unit": "",
        "item": "salt flakes and freshly ground black pepper"
      },
      {
        "qty": null,
        "unit": "",
        "item": "short-grain rice, steamed using absorption method, to serve"
      },
      {
        "qty": 50,
        "unit": "ml",
        "item": "light soy sauce (dressing)"
      },
      {
        "qty": 40,
        "unit": "ml",
        "item": "fish sauce (dressing)"
      },
      {
        "qty": 30,
        "unit": "ml",
        "item": "extra virgin olive oil (dressing)"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "orange, zest and juice (dressing)"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "lemon, zest and juice (dressing)"
      },
      {
        "qty": 2,
        "unit": "",
        "item": "spring onions, white part only, finely sliced (dressing)"
      },
      {
        "qty": 1,
        "unit": "",
        "item": "long green chilli, finely sliced into rounds (dressing)"
      },
      {
        "qty": 1,
        "unit": "tsp",
        "item": "chilli flakes (dressing)"
      }
    ],
    "method": [
      "Remove the salmon from the refrigerator 30 minutes prior to cooking.",
      "Combine the dressing ingredients in a small bowl.",
      "Toss the fennel and spring onions in half the olive oil. Heat a large frying pan over high heat and, working in batches, lay the fennel in a single layer and cook about 2 minutes until a little blackened, then toss and cook about 2 minutes more — it should have colour but still retain some crunch. Do the same for the spring onions. In a separate bowl, toss the fennel in the vinegar. Season both fennel and spring onions with salt and pepper.",
      "Preheat the oven to 180°C fan-forced (200°C conventional).",
      "Lay the salmon skin-side down on a baking tray and smooth over the remaining oil. Season generously with salt and pepper and roast 15 minutes, then rest 5 minutes. It will be a little rare on top but will continue to cook as it rests.",
      "Transfer the salmon to a platter, scatter the fennel and spring onions around one edge, then pour over the dressing. Fork the salmon apart and serve with rice."
    ],
    "notes": [
      {
        "text": "Cooked gently as one large piece — after resting it will be rare but falls apart in flakes. For a more cooked result, increase cooking time by about 8 minutes.",
        "date": "2026-07-31"
      },
      {
        "text": "The sweet, tangy, salty dressing lifts the rich salmon, and the citrus notes work beautifully with the charred fennel.",
        "date": "2026-07-31"
      },
      {
        "text": "Ingredients marked (dressing) are the citrus and soy dressing — combined separately and poured over at the end.",
        "date": "2026-07-31"
      }
    ],
    "pairings": [
      {
        "text": "Steamed short-grain rice, as the recipe intends.",
        "date": "2026-07-31"
      }
    ],
    "createdAt": "2026-07-31T00:00:00.000Z",
    "pos": 3
  }
]
